<template>
  <div class="gf-commit-line-graph">
    <canvas ref="commitChart"></canvas>
    <div class="gf-commit-line-graph__info mt-2">
      <span class="gf-commit-line-graph__info-line"></span>
      <b>Коммиты</b>
      <div class="gf-commit-line-graph__info-stat">
        <span>Средн.: {{ commitData?.avgCommits }}</span>
        <span class="gf-dot-xs border-dark"></span>
        <span>Макс.: {{ commitData?.maxCommits }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import 'chartjs-adapter-moment';
import InsightsMessages from '@/shared-modules/modules/insights/insights.messages';

export default {
  name: 'GfCommitLineGraph',
  props: {
    commitData: {
      type: Object,
      default() {
        return {};
      }
    },
    maxCommitsNumber: {
      type: Number,
      default: 0,
      required: true
    }
  },
  data() {
    return {
      config: {},
      chart: null,
      colors: {},
      timeUnit: 'day'
    };
  },
  computed: {
    formattedData() {
      const data = {
        labels: [],
        datasets: [
          {
            data: [],
            fill: true,
            tension: 0.1,
            pointRadius: 0,
            pointBackgroundColor: this.colors.gfPrimaryColor,
            borderWidth: 2,
            borderColor: this.colors.gfPrimaryColor,
            backgroundColor: this.colors.gfPrimaryTransparentColor
          }
        ]
      };

      const keys = Object.keys(this.commitData?.dateCommitMap);
      const dataArray = keys.map((key) => [key, this.commitData?.dateCommitMap[key]]).sort();

      dataArray.forEach((item) => {
        const key = item[0];
        const value = item[1];
        if (data.labels[0] !== key) data.labels.push(key);
        data.datasets[0].data.push({ x: key, y: value });
      });

      if (data?.labels?.length > 30) this.timeUnit = 'month';
      return data;
    },
    colorSchemeVersion() {
      return this.$store.getters.getColorScheme;
    }
  },
  watch: {
    commitData() {
      this.updateChart();
    },
    colorSchemeVersion() {
      this.updateColors();
      this.updateChart();
    }
  },
  mounted() {
    this.updateColors();
    this.createChart();
  },
  methods: {
    createChart() {
      this.createConfig();
      this.chart = new Chart(this.$refs.commitChart, this.config);
    },
    updateChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      this.createChart();
    },
    updateColors() {
      this.colors.gfPrimaryColor = getComputedStyle(document.body).getPropertyValue('--primary');
      this.colors.gfTextColor = getComputedStyle(document.body).getPropertyValue('--text');
      this.colors.gfTextMutedColor = getComputedStyle(document.body).getPropertyValue(
        '--text-muted'
      );
      this.colors.gfBorderColor = getComputedStyle(document.body).getPropertyValue(
        '--border-color'
      );
      this.colors.gfPrimaryTransparentColor = getComputedStyle(document.body).getPropertyValue(
        '--primary-transparent'
      );
    },
    createConfig() {
      const verticalHoverLine = {
        id: 'verticalHoverLine',
        beforeDatasetsDraw: (chart) => {
          const {
            ctx,
            chartArea: { top, bottom }
          } = chart;
          ctx.save();
          chart.getDatasetMeta(0).data.forEach((dataPoint) => {
            if (dataPoint.active === true) {
              ctx.beginPath();
              ctx.strokeStyle = this.colors.gfTextColor;
              ctx.lineWidth = 1;
              ctx.moveTo(dataPoint.x, top);
              ctx.lineTo(dataPoint.x, bottom);
              ctx.stroke();
            }
          });
        }
      };
      this.config = {
        type: 'line',
        data: this.formattedData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          devicePixelRatio: 2,
          plugins: {
            legend: {
              display: false
            }
          },
          interaction: {
            mode: 'index',
            intersect: false,
            position: 'nearest'
          },
          scales: {
            x: {
              type: 'time',
              time: {
                tooltipFormat: 'LL',
                unit: this.timeUnit,
                minUnit: this.timeUnit,
                round: true
              },
              grid: {
                display: false
              },
              ticks: {
                color: this.colors.gfTextMutedColor
              }
            },
            y: {
              stacked: true,
              max: this.maxCommitsNumber,
              min: 0,
              title: {
                display: true,
                text: this.$t('commits.number'),
                color: this.colors.gfTextColor
              },
              grid: {
                color: this.colors.gfBorderColor
              },
              ticks: {
                beginAtZero: true,
                color: this.colors.gfTextMutedColor,
                precision: 0
              }
            }
          },
          tooltips: {
            enabled: true,
            intersect: false,
            mode: 'index',
            position: 'nearest'
          },
          hover: {
            mode: 'index',
            intersect: false
          }
        },
        plugins: [verticalHoverLine]
      };
    }
  },
  i18n: {
    messages: InsightsMessages
  }
};
</script>

<style lang="scss">
$this: '.gf-commit-line-graph';

#{$this} {
  canvas {
    height: 300px !important;
    width: 100% !important;
  }
  #{$this}__info {
    margin-left: 43px;
    display: flex;
    gap: 8px;
    align-items: center;

    &-line {
      height: 4px;
      width: 18px;
      border: 2px solid var(--primary);
      background-color: var(--primary);
    }
    &-stat {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
}
</style>
